import { useMutation } from "@tanstack/react-query";
import { fromBech32 } from "@cosmjs/encoding";
import { useMemo } from "react";
import { WalletType } from "../shared/wallet";
import { useChainStore, useAccountStore } from "../shared/store";
import { ConnectedAgentType, } from "../shared/account";
import { allChainsCallAction, decideChainAction } from "./chain";
import { ErrorMessage } from "./error";
export { ConnectedAgentType, isCosmosAccount, isEthereumAccount, } from "../shared/account";
export const getAccount = () => {
    const { chainId } = useChainStore.getState();
    return useAccountStore.getState().activeAccount(chainId);
};
export const useAccount = (chainId) => {
    const defaultChainId = useChainStore((state) => state.chainId);
    const accountChainId = chainId ?? defaultChainId;
    const account = useAccountStore((state) => state.activeAccount(accountChainId));
    return useMemo(() => {
        if (account?.chainId !== accountChainId) {
            return {
                data: undefined,
            };
        }
        return {
            data: account,
        };
    }, [account, accountChainId]);
};
export const useMainAccount = () => {
    const chainId = useChainStore((state) => state.chainId);
    return useAccountStore((state) => state.mainAccount(chainId));
};
export const useViewingAccounts = () => {
    const chainId = useChainStore((state) => state.chainId);
    const accounts = useAccountStore((state) => state.accounts);
    return accounts.filter((account) => {
        return account.walletType === "viewing" && account.chainId === chainId;
    });
};
export const viewingConnectedAgent = (account) => {
    return {
        type: ConnectedAgentType.generic,
        account,
        signAndBroadcast: () => {
            throw new Error(ErrorMessage.viewingExecute);
        },
    };
};
export const useConnect = (args = {}) => {
    const { onError, onLoading, onSuccess } = args;
    const mutation = useMutation({
        mutationKey: ["useConnect", onError, onLoading, onSuccess],
        mutationFn: async (strategy) => {
            useAccountStore.setState({ status: "connecting" });
            const { chainId } = useChainStore.getState();
            if (strategy.walletType === WalletType.viewing) {
                return viewingConnectedAgent({
                    walletType: strategy.walletType,
                    address: strategy.address,
                    chainId,
                });
            }
            const action = await decideChainAction({
                chainId,
                cosmos: async () => {
                    const { cosmosConnect } = await import("../cosmos/account");
                    return cosmosConnect;
                },
                injective: async () => {
                    const { injectiveConnect } = await import("../injective/account");
                    return injectiveConnect;
                },
                rujira: async () => {
                    const { rujiraConnect } = await import("../rujira/account");
                    return rujiraConnect;
                },
            });
            const connect = await action();
            return connect(strategy);
        },
        onMutate: onLoading,
        onError: (error, strategy) => {
            onError?.(error, strategy);
            useAccountStore.setState({
                status: "disconnected",
                connectIssue: {
                    error,
                    walletType: strategy.walletType,
                },
            });
        },
        onSuccess: (connectedAgent) => {
            useAccountStore.getState().addAccount(connectedAgent.account);
            useAccountStore.setState({ status: "connected" });
            return onSuccess?.(connectedAgent);
        },
    });
    return useMemo(() => ({
        connect: async (strategy) => {
            try {
                return await mutation.mutateAsync(strategy);
            }
            catch {
                return;
            }
        },
        error: mutation.error,
        isLoading: mutation.isPending,
        isSuccess: mutation.isSuccess,
        status: mutation.status,
    }), [
        mutation.mutateAsync,
        mutation.error,
        mutation.isPending,
        mutation.isSuccess,
        mutation.status,
    ]);
};
export const useDisconnect = (args = {}) => {
    const { onError, onLoading, onSuccess } = args;
    const mutation = useMutation({
        mutationKey: ["useDisconnect", onError, onLoading, onSuccess],
        mutationFn: async (address) => {
            if (address) {
                useAccountStore.getState().removeAccount(address);
            }
            else {
                useAccountStore.setState({
                    status: "disconnected",
                    accounts: [],
                    connectIssue: undefined,
                });
                await allChainsCallAction({
                    cosmos: async () => {
                        const { cosmosDisconnect } = await import("../cosmos/account");
                        return cosmosDisconnect();
                    },
                    injective: async () => {
                        const { injectiveDisconnect } = await import("../injective/account");
                        return injectiveDisconnect();
                    },
                    rujira: async () => {
                        const { rujiraDisconnect } = await import("../rujira/account");
                        return rujiraDisconnect();
                    },
                });
            }
        },
        onMutate: onLoading,
        onError: (error) => onError?.(error, undefined),
        onSuccess: () => onSuccess?.(undefined),
    });
    return useMemo(() => ({
        disconnect: async (address) => {
            try {
                return await mutation.mutateAsync(address);
            }
            catch {
                return;
            }
        },
        error: mutation.error,
        isLoading: mutation.isPending,
        isSuccess: mutation.isSuccess,
        status: mutation.status,
    }), [
        mutation.mutateAsync,
        mutation.error,
        mutation.isPending,
        mutation.isSuccess,
        mutation.status,
    ]);
};
const getAddressPrefix = (address) => {
    try {
        const decoded = fromBech32(address);
        return { type: "cosmos", prefix: decoded.prefix };
    }
    catch {
        if (address.startsWith("0x")) {
            return { type: "ethereum", prefix: "0x" };
        }
    }
};
export const truncateAddress = (address, startIndex, endIndex) => {
    const lowercaseAddress = address.toLowerCase();
    const addressPrefix = getAddressPrefix(lowercaseAddress)?.prefix ?? "";
    const defaultIndexes = (() => {
        switch (getAddressPrefix(lowercaseAddress)?.type) {
            case "cosmos":
                return { start: 2, end: 4 };
            case "ethereum":
                return { start: 4, end: 5 };
            case undefined:
                return { start: 4, end: 4 };
        }
    })();
    const indexes = {
        start: startIndex ?? defaultIndexes.start,
        end: endIndex ?? defaultIndexes.end,
    };
    if (addressPrefix.length + indexes.start + indexes.end >= address.length) {
        return address;
    }
    const start = address.slice(0, addressPrefix.length + indexes.start);
    const end = address.slice(-indexes.end, address.length);
    return `${start}...${end}`;
};
//# sourceMappingURL=account.js.map